import {ChangeEvent, FC, useMemo} from 'react'
import {Form} from 'react-bootstrap'
import {Calendar3, Clock} from 'react-bootstrap-icons'
import {useTranslation} from 'react-i18next'
import ReactDatePicker from 'react-datepicker'
import {getLocaleConfig} from '@utils/date/locales.ts'
import {formatForInputDateTime, parseDate} from '@utils/date/formatters.ts'
import 'react-datepicker/dist/react-datepicker.css'
import './DatePicker.css'

const isTouchDevice = (): boolean => {
    return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        // @ts-expect-error - msMaxTouchPoints is IE-specific
        navigator.msMaxTouchPoints > 0
    )
}

const getDateTimeFormat = (locale: string): string => {
    const formats: Record<string, string> = {
        fi: 'dd.MM.yyyy HH:mm',
        en: 'MM/dd/yyyy h:mm aa',
        sv: 'yyyy-MM-dd HH:mm',
        th: 'dd/MM/yyyy HH:mm'
    }
    return formats[locale] || formats.en
}

const getTimeFormat = (locale: string): string => {
    const formats: Record<string, string> = {
        fi: 'HH:mm',
        en: 'h:mm aa',
        sv: 'HH:mm',
        th: 'HH:mm'
    }
    return formats[locale] || formats.en
}

interface DateTimePickerProps {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    isInvalid?: boolean;
    className?: string;
    placeholder?: string;
    timeIntervals?: number;
    minDate?: Date;
    maxDate?: Date;
    openToDate?: Date;
    onFocus?: () => void;
}

const DateTimePicker: FC<DateTimePickerProps> = ({
                                                     id,
                                                     value,
                                                     onChange,
                                                     disabled = false,
                                                     isInvalid = false,
                                                     className = '',
                                                     placeholder,
                                                     timeIntervals = 15,
                                                     minDate,
                                                     maxDate,
                                                     openToDate,
                                                     onFocus,
                                                     ...rest
                                                 }) => {
    const {i18n} = useTranslation()
    const locale = i18n.language?.split('-')[0] || 'en'
    const localeConfig = useMemo(() => getLocaleConfig(locale), [locale])
    const dateTimeFormat = useMemo(() => getDateTimeFormat(locale), [locale])
    const timeFormat = useMemo(() => getTimeFormat(locale), [locale])

    const dateTimeValue = useMemo(() => {
        if (!value) return null
        const parsed = parseDate(value)
        return parsed && !isNaN(parsed.getTime()) ? parsed : null
    }, [value])

    const handleDateTimeChange = (date: Date | null): void => {
        if (!date || isNaN(date.getTime())) {
            onChange('')
            return
        }
        const isoString = formatForInputDateTime(date)
        onChange(isoString)
    }

    if (isTouchDevice()) {
        return (
            <Form.Control
                id={id}
                type="datetime-local"
                value={value || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                disabled={disabled}
                isInvalid={isInvalid}
                className={className}
                min={minDate ? formatForInputDateTime(minDate) : undefined}
                max={maxDate ? formatForInputDateTime(maxDate) : undefined}
                onFocus={onFocus}
                autoComplete="off"
                {...rest}
            />
        )
    }

    return (
        <div className="position-relative">
            <ReactDatePicker
                id={id}
                selected={dateTimeValue}
                onChange={handleDateTimeChange}
                disabled={disabled}
                dateFormat={dateTimeFormat}
                timeFormat={timeFormat}
                locale={localeConfig.dateFnsLocale}
                className={`form-control ${isInvalid ? 'is-invalid' : ''} ${className}`}
                placeholderText={placeholder}
                showTimeSelect
                timeIntervals={timeIntervals}
                timeCaption={locale === 'fi' ? 'Aika' : locale === 'sv' ? 'Tid' : 'Time'}
                minDate={minDate}
                maxDate={maxDate}
                openToDate={openToDate}
                popperProps={{strategy: 'fixed'}}
                popperPlacement="bottom-start"
                onFocus={onFocus}
                autoComplete="off"
                {...rest}
            />
            <div className="position-absolute top-50 end-0 translate-middle-y me-2 d-flex gap-1"
                 style={{pointerEvents: 'none'}}>
                <Calendar3 className="text-muted" size={16}/>
                <Clock className="text-muted" size={16}/>
            </div>
        </div>
    )
}

export default DateTimePicker
